import React, { useRef } from "react"
import get from "lodash/get"
import InputComponent from "components/FormControls/Input/Input"
import LinkInputComponent from "components/FormControls/LinkInput/LinkInput"
import CheckboxComponent from "components/Checkbox/Checkbox"
import WysiwygComponent from "components/FormControls/Wysiwyg/Wysiwyg"
import AutocompleteComponent from "components/Autocomplete/Autocomplete"
import RemoteAutocompleteComponent from "components/Autocomplete/RemoteAutocomplete"
import LocalI18nAutocompleteComponent from "components/Autocomplete/LocalI18nAutocomplete"
import DatePickerComponent from "components/FormControls/DatePicker/DatePicker"
import DateTimePickerComponent from "components/FormControls/DatePicker/DateTimePicker"
import FilePickerComponent from "components/FormControls/FilePicker/FilePicker"
import CompactColorPickerComponent from "components/FormControls/ColorPicker/ColorPicker"
import { I18n } from "utils/i18n-shim"
import isNil from "lodash/isNil"
import SwitchComponent from "@mui/material/Switch"

export const createWrapComponent =
  ({ skipBlur = false, getValue = (x) => x } = {}) =>
  (Component) =>
  ({ field, form, ...rest }) => {
    if (!form) {
      throw new Error(`${Component.name} was used outside of the <Formik>`)
    }

    const justChanged = useRef(false)

    let error =
      (get(form.touched, field.name) || rest.showErrorsPristine) &&
      get(form.errors, field.name)

    if (error instanceof Array) {
      error = error.filter((err) => !!err)[0]
    }

    let errorText = null

    if (!isNil(error)) {
      if (error instanceof Object) {
        errorText = I18n.t(`errors.${error[Object.keys(error)[0]]}`)
      } else {
        errorText = I18n.t(`errors.${error}`)
      }
    }

    const handleBlur = (e, ...values) => {
      if (!rest.skipBlurTouch && e) field.onBlur(e, ...values)
      if (rest.onBlur) rest.onBlur(e, ...values)
    }

    const onBlur = (e, ...values) => {
      if (justChanged.current) {
        // Blur fired synchronously after onChange (e.g. MUI blurOnSelect).
        // Skip field.onBlur — it would run setFieldTouched with stale state.values
        // before setFieldValue's dispatch is processed, causing a spurious error.
        // The field is already touched (set in onChange); setFieldValue handles validation.
        justChanged.current = false
        rest.onBlur && rest.onBlur(e, ...values)
      } else {
        form.setFieldTouched(field.name)
        !skipBlur && handleBlur(e, ...values)
      }
    }

    const onChange = (...args) => {
      justChanged.current = true
      // Reset after the current synchronous call stack so future blur events
      // (triggered by the user clicking away) validate normally.
      Promise.resolve().then(() => {
        justChanged.current = false
      })
      form.setFieldValue(field.name, getValue(...args))
      form.setFieldTouched(field.name, true, false)
      rest.onChange && rest.onChange(...args)
    }

    return (
      <Component
        {...field}
        {...rest}
        errors={get(form.errors, field.name)}
        error={error}
        onBlur={onBlur}
        onChange={onChange}
        helperText={errorText}
      />
    )
  }

export const wrapComponent = createWrapComponent()

export const Input = wrapComponent((props) => (
  <InputComponent {...props} debounceTimeout={0} noCollectableText />
))

export const LinkInput = wrapComponent(LinkInputComponent)
export const Wysiwyg = createWrapComponent({ skipBlur: true })(WysiwygComponent)
export const DatePicker = wrapComponent(DatePickerComponent)
export const DateTimePicker = wrapComponent(DateTimePickerComponent)
export const FilePicker = wrapComponent(FilePickerComponent)
export const CompactColorPicker = wrapComponent(CompactColorPickerComponent)

export const Autocomplete = wrapComponent(AutocompleteComponent)
export const RemoteAutocomplete = wrapComponent(RemoteAutocompleteComponent)
export const LocalI18nAutocomplete = wrapComponent(
  LocalI18nAutocompleteComponent
)
export const Switch = ({ form, field, ...rest }) => {
  return (
    <SwitchComponent
      {...rest}
      checked={!!field.value}
      name={field.name}
      color="primary"
      onChange={(e) => {
        form.setFieldValue(field.name, e.target.checked)
        form.setFieldTouched(field.name)
        rest.onChange && rest.onChange(e.target.checked)
      }}
    />
  )
}

export const Checkbox = ({ form, field, ...rest }) => {
  return (
    <CheckboxComponent
      {...rest}
      checked={!!field.value}
      name={field.name}
      onChange={(e) => {
        form.setFieldValue(field.name, e.target.checked)
        form.setFieldTouched(field.name)
        rest.onChange && rest.onChange(e.target.checked)
      }}
    />
  )
}

export { default as NumberInput } from "./NumberInput"
